import axios from 'axios';

export default class ApiHandler {
    constructor() {
        this.api_url = (process.env.REACT_APP_API_URL || "http://localhost:3030").replace(/\/+$/, "");
        this.client = axios.create();
        this.loggedIn = false;
        this.user = null;
    }

    _finalize(body) {
        if (body && body.success === false && body.msg === 'Session expired.') this.setLoggedIn(false);
        if (body && body.success === true) this.setLoggedIn(true);
        return body;
    }

    async _post(url, query = {}) {
        try {
            const res = await this.client.post(
                `${this.api_url}/api/v1/${url}`,
                query,
                { withCredentials: true }
            );
            return this._finalize(res?.data ?? res);
        } catch (error) {
            console.log(error);
            return { success: false, msg: 'Interner Fehler' };
        }
    }

    async _request(url, query = {}) {
        try {
            const res = await this.client.get(
                `${this.api_url}/api/v1/${url}`,
                { params: query, withCredentials: true }
            );
            return this._finalize(res?.data ?? res);
        } catch (error) {
            console.log(error);
            return { success: false, msg: 'Interner Fehler' };
        }
    }

    setLoggedIn(log) { this.loggedIn = log; }

    async getLoggedIn() {
        const currUser = await this.getCurrentUser();
        this.user = currUser?.msg || null;
        this.loggedIn = !!currUser?.success;
        return this.loggedIn;
    }

    async login(username, password) {
        const res = await this._post("auth", { username, password });
        if (res?.success) await this.getLoggedIn();
        return res;
    }

    async logout() {
        const res = await this._request("logout");
        this.user = null;
        this.loggedIn = false;
        return res;
    }

    async getCurrentUser() { return await this._request("user/current"); }
    async getUser(userId) { return await this._request("user/" + userId); }
    async getUsers() { return await this._request("users"); }
    async getUserNames() { return await this._request("user-names"); }
    async hasRight(right) { return await this._request("rights/" + right); }

    async createUser(user) { return await this._post("user/create", { user }); }
    async updateUser(user) { return await this._post("user/update", { user }); }
    async deleteUser(user) { return await this._post("user/delete", { _id: user?._id || user }); }
    async changePassword(userId, oldPassword, newPassword) {
        return await this._post("user/update/password", { _id: userId, oldPassword, newPassword });
    }
    async changeRights(userId, rights) {
        return await this._post("user/update", { user: { _id: userId, rights } });
    }
    async changeName(userId, name) {
        return await this._post("user/update", { user: { _id: userId, name } });
    }
    async changeDescription(userId, description) {
        return await this._post("user/update", { user: { _id: userId, description } });
    }

    async getJobs(query = {}) { return await this._request("jobs", query); }
    async getJob(jobId) { return await this._request("job/" + jobId); }
    async createJob(job) { return await this._post("job/create", { job }); }
    async updateJob(job) { return await this._post("job/update", { job }); }
    async deleteJob(jobNr) { return await this._post("job/delete", { nr: jobNr }); }

    async getSecureEntries() { return await this._request("secure-entries"); }
    async createSecureEntry(entry) { return await this._post("secure-entries", { entry }); }
    async cloneSecureEntry(akz, jobNr) { return await this._post("secure-entries/clone", { akz, jobNr }); }
    async resolveSecureEntries(akzList) { return await this._post("secure-entries/resolve", { akzList }); }
    async updateSecureEntry(akz, fields) { return await this._post("secure-entries/update", { akz, fields }); }

    async downloadJobsCsv() {
        try {
            const res = await this.client.get(
                `${this.api_url}/api/v1/jobs/export/csv`,
                { responseType: 'blob', withCredentials: true }
            );

            const contentType = res.headers?.['content-type'] || '';
            if (!contentType.includes('application/zip')) {
                const text = await res.data.text();
                let parsed = { success: false, msg: 'Unbekannter Fehler' };
                try { parsed = JSON.parse(text); } catch (_) {}
                return this._finalize(parsed);
            }

            const blob = new Blob([res.data], { type: 'application/zip' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Betriebsauftraege_${new Date().toISOString().slice(0, 10)}.zip`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);

            return { success: true };
        } catch (error) {
            console.log(error);
            return { success: false, msg: 'Export konnte nicht geladen werden.' };
        }
    }

    async downloadJobPdf(jobNr) {
        try {
            const res = await this.client.get(
                `${this.api_url}/api/v1/job/${jobNr}/pdf`,
                { responseType: 'blob', withCredentials: true }
            );

            const contentType = res.headers?.['content-type'] || '';
            if (!contentType.includes('application/pdf')) {
                const text = await res.data.text();
                let parsed = { success: false, msg: 'Unbekannter Fehler' };
                try { parsed = JSON.parse(text); } catch (_) {}
                return this._finalize(parsed);
            }

            const blob = new Blob([res.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Betriebsauftrag_x${String(jobNr).padStart(4, '0')}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);

            return { success: true };
        } catch (error) {
            console.log(error);
            return { success: false, msg: 'PDF konnte nicht geladen werden.' };
        }
    }
}
